import { InlineEditableText } from "components/ds/inline-editable-text";
import type { TableColumnDef, TableStatus } from "components/ds/table";
import { Table } from "components/ds/table";
import { useMemo, useState } from "react";
import intl from "react-intl-universal";
import { DATA_FORMAT_TYPES, getDataFormat } from "utils/format-date";
import { formatErrorMessage } from "utils/handle-error";
import { useProductionPlanSupplierContext } from "../../context";
import { TableCellText } from "../../styled";
import type { IProductionPlanSupplier } from "../../types";
import {
  formatProductionPlanSupplierNumericValue,
  getProductionPlanSupplierDates,
  parseProductionPlanSupplierNumericValue,
} from "../../utils";
import DeleteConfirmationModal from "./delete-confirmation-modal";
import LastUpdateCell from "./last-update-cell";

type Props = Readonly<{ suppliers: IProductionPlanSupplier[] }>;



const renderReadOnlyCell = (value: unknown) => {
  const text = value === null || value === undefined || value === "" ? "-" : String(value);

  return <TableCellText title={text}>{text}</TableCellText>;
};

const renderDescriptionWithCode = (code: string, description: string) => (
  <TableCellText title={`${code} - ${description}`}>{`${code} - ${description}`}</TableCellText>
);

type LastUpdateColumnCellProps = Readonly<{
  value: string | null | undefined;
}>;

const LastUpdateColumnCell = ({ value }: LastUpdateColumnCellProps) =>
  value ? <LastUpdateCell value={value} /> : renderReadOnlyCell("-");

const getQuantityValidationMessage = (value: number | null, allowEmpty = true) => {
  if (value === null) {
    return allowEmpty ? null : intl.get("productionPlanSupplier.validation.valueRequired");
  }

  if (Number.isNaN(value) || value < 0) {
    return intl.get("productionPlanSupplier.validation.nonNegativeValue");
  }

  return null;
};

const getProductionQuantityValue = (supplier: IProductionPlanSupplier, productionDate?: string) =>
  productionDate
    ? (supplier.ProductionDates.find((item) => item.ProductionDate === productionDate)
        ?.ProductionQuantity ?? null)
    : null;

type QuantityEditableCellProps = Readonly<{
  handleUpdateSupplierField: (
    supplierId: number,
    update: {
      field: "CurrentStock" | "Forecast" | "ProductionQuantity";
      value: number | null;
      productionDate?: string;
    }
  ) => Promise<void>;
  field: "CurrentStock" | "Forecast" | "ProductionQuantity";
  supplier: IProductionPlanSupplier;
  productionDate?: string;
  allowEmpty?: boolean;
}>;

const QuantityEditableCell = ({
  handleUpdateSupplierField,
  field,
  supplier,
  productionDate,
  allowEmpty = true,
}: QuantityEditableCellProps) => {
  const currentValue =
    field === "ProductionQuantity"
      ? getProductionQuantityValue(supplier, productionDate)
      : supplier[field];
  const fieldLabel = intl.get(
    `productionPlanSupplier.table.${field.charAt(0).toLowerCase() + field.slice(1)}`
  );
  const ariaLabel = intl.get("productionPlanSupplier.table.editField", {
    field: fieldLabel,
    supplier: supplier.SupplierCode,
    date: productionDate ? ` ${productionDate}` : "",
  });

  return (
    <InlineEditableText<number | null>
      value={currentValue}
      ariaLabel={ariaLabel}
      inputMode="decimal"
      type="number"
      formatDisplay={formatProductionPlanSupplierNumericValue}
      parse={parseProductionPlanSupplierNumericValue}
      validate={(nextValue) => getQuantityValidationMessage(nextValue, allowEmpty)}
      onCommit={async (nextValue) => {
        await handleUpdateSupplierField(supplier.Id, {
          field,
          value: nextValue,
          productionDate,
        });
      }}
    />
  );
};

type BuildProductionPlanSupplierColumnsParams = Readonly<{
  handleUpdateSupplierField: QuantityEditableCellProps["handleUpdateSupplierField"];
  productionDates: string[];
}>;

const buildProductionPlanSupplierColumns = ({
  handleUpdateSupplierField,
  productionDates,
}: BuildProductionPlanSupplierColumnsParams): Array<TableColumnDef<IProductionPlanSupplier>> => {
  const staticColumns: Array<TableColumnDef<IProductionPlanSupplier>> = [
    {
      id: "Supplier",
      header: intl.get("productionPlanSupplier.table.supplierDescription"),
      size: 188,
      accessorFn: (row) => `${row.SupplierCode} - ${row.SupplierDescription}`,
      cell: ({ row }) =>
        renderDescriptionWithCode(row.original.SupplierCode, row.original.SupplierDescription),
    },
    {
      id: "Material",
      header: intl.get("productionPlanSupplier.table.materialDescription"),
      accessorFn: (row) =>
        row.MaterialNumber ? `${row.MaterialNumber} - ${row.MaterialDescription ?? ""}` : "-",
      cell: ({ row }) =>
        row.original.MaterialNumber
          ? renderDescriptionWithCode(
              row.original.MaterialNumber,
              row.original.MaterialDescription ?? "-"
            )
          : renderReadOnlyCell("-"),
    },
    {
      accessorKey: "Family",
      header: intl.get("productionPlanSupplier.table.family"),
      cell: ({ getValue }) => renderReadOnlyCell(getValue()),
    },
    {
      id: "LabeledFamilyName",
      header: intl.get("commons.labeledFamily"),
      size: 188,
      accessorFn: (row) => row.LabeledFamilyName ?? "-",
      cell: ({ row }) => renderReadOnlyCell(row.original.LabeledFamilyName ?? "-"),
    },
    {
      id: "SupplierOriginName",
      header: intl.get("productionPlanSupplier.table.supplierOrigin"),
      accessorFn: (row) => row.SupplierOriginName ?? "-",
      size: 188,
      cell: ({ row }) => renderReadOnlyCell(row.original.SupplierOriginName ?? "-"),
    },
    {
      accessorKey: "CurrentStock",
      header: intl.get("productionPlanSupplier.table.currentStock"),
      cell: ({ row }) => (
        <QuantityEditableCell
          handleUpdateSupplierField={handleUpdateSupplierField}
          field="CurrentStock"
          supplier={row.original}
          allowEmpty={false}
        />
      ),
    },
    {
      accessorKey: "Forecast",
      header: intl.get("productionPlanSupplier.table.forecast"),
      cell: ({ row }) => (
        <QuantityEditableCell
          handleUpdateSupplierField={handleUpdateSupplierField}
          field="Forecast"
          supplier={row.original}
        />
      ),
    },
  ];

  const dateColumns: Array<TableColumnDef<IProductionPlanSupplier>> = productionDates.map(
    (date) => ({
      id: `production-date-${date}`,
      header: getDataFormat(date, DATA_FORMAT_TYPES.WITH_YEAR),
      enableHiding: false,
      size: 115,
      accessorFn: (row) => getProductionQuantityValue(row, date),
      cell: ({ row }) => (
        <QuantityEditableCell
          handleUpdateSupplierField={handleUpdateSupplierField}
          field="ProductionQuantity"
          supplier={row.original}
          productionDate={date}
        />
      ),
    })
  );

  const lastUpdateColumn: TableColumnDef<IProductionPlanSupplier> = {
    id: "LastUpdate",
    header: "",
    enableHiding: false,
    size: 50,
    accessorFn: (row) => row.LastUpdate,
    cell: ({ row }) => <LastUpdateColumnCell value={row.original.LastUpdate} />,
  };

  return [...staticColumns, ...dateColumns, lastUpdateColumn];
};

const ProductionPlanSupplierTable = ({ suppliers }: Props) => {
  const {
    filters,
    pagination,
    handleDeleteSelectedRows,
    handleUpdateSupplierField,
    searchProductionPlanData,
  } = useProductionPlanSupplierContext();
  const [rowsToDelete, setRowsToDelete] = useState<IProductionPlanSupplier[]>([]);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const productionDates = useMemo(() => getProductionPlanSupplierDates(suppliers), [suppliers]);

  const columns = useMemo<Array<TableColumnDef<IProductionPlanSupplier>>>(
    () => buildProductionPlanSupplierColumns({ handleUpdateSupplierField, productionDates }),
    [handleUpdateSupplierField, productionDates]
  );

  const status: TableStatus = suppliers.length ? "ready" : "empty";

  const handlePaginationChange = async ({
    pageIndex,
    pageSize,
  }: {
    pageIndex: number;
    pageSize: number;
  }) => {
    await searchProductionPlanData({
      ...filters,
      _page: pageIndex,
      _size: pageSize,
    });
  };

  const tablePagination =
    pagination.totalItems > 0
      ? {
          ...pagination,
          onPaginationChange: handlePaginationChange,
        }
      : undefined;

  const handleRequestDelete = (selectedRows: IProductionPlanSupplier[]) => {
    setDeleteError(null);
    setRowsToDelete(selectedRows);
  };

  const handleCloseDelete = () => {
    setRowsToDelete([]);
    setDeleteError(null);
  };

  const handleConfirmDelete = async () => {
    if (!rowsToDelete.length) {
      return;
    }

    setDeleteError(null);
    try {
      await handleDeleteSelectedRows(rowsToDelete);
      setRowsToDelete([]);
    } catch (error) {
      setDeleteError(formatErrorMessage(error));
    }
  };

  return (
    <>
      <Table
        ariaLabel={intl.get("productionPlanSupplier.title")}
        columns={columns}
        data={suppliers}
        enableRowSelection
        status={status}
        getRowId={(supplier, index) =>
          `${supplier.Id}-${supplier.SupplierId}-${supplier.MaterialNumber}-${index}`
        }
        onSelectedRowsDelete={handleRequestDelete}
        pagination={tablePagination}
        initialColumnPinning={{
          left: [
            "Supplier",
            "Material",
            "Family",
            "LabeledFamilyName",
            "SupplierOriginName",
            "CurrentStock",
            "Forecast",
          ],
          right: ["LastUpdate"],
        }}
      />
      <DeleteConfirmationModal
        open={rowsToDelete.length > 0}
        count={rowsToDelete.length}
        error={deleteError}
        onClose={handleCloseDelete}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

export default ProductionPlanSupplierTable;
